/**
 * Read/write a layer's media pointer (`meta.mediaId`). In a DESIGN it holds an
 * entry id into `designs.media`; in a TEMPLATE it holds a media id (D-47) —
 * this module is id-space agnostic, the caller decides which it writes.
 *
 * Readers are WIDENED (`meta: unknown` in) per the narrowed-meta rule: layer
 * meta types are augmented, and `next build` rejects assumptions `tsc` allows.
 * Use updaters as `prev ? withMediaId(prev, id) : prev`.
 */

export function readMediaId(meta: unknown): string | undefined {
  const mediaId = (meta as { mediaId?: unknown } | null | undefined)?.mediaId;
  return typeof mediaId === "string" && mediaId.length > 0
    ? mediaId
    : undefined;
}

/**
 * The temporary id of an upload still in flight on this layer
 * (`meta.asset.importStatus === "uploading"`). While it is set, the layer
 * DISPLAYS the import — its blob is seeded under this id — even though
 * `meta.mediaId` still names the image it is replacing: that pointer stays
 * until the upload lands, so an autosave mid-upload keeps the old image.
 */
export function readImportingMediaId(meta: unknown): string | undefined {
  const asset = (meta as { asset?: unknown } | null | undefined)?.asset as
    | { id?: unknown; importStatus?: unknown }
    | null
    | undefined;
  return asset?.importStatus === "uploading" &&
    typeof asset.id === "string" &&
    asset.id
    ? asset.id
    : undefined;
}

/**
 * Point the layer at `mediaId` and drop the legacy asset pointer
 * (`meta.asset.id`) and its persisted signed URL (`meta.asset.preview`).
 * Display-only fields on `meta.asset` (name, dimensions) survive.
 */
export function withMediaId<M extends object>(meta: M, mediaId: string) {
  return { ...stripLegacyAssetPointer(meta), mediaId };
}

/**
 * Take a layer's image off it entirely: the pointer, the persisted `value`,
 * and — for an image FILL — the `url(...)` in `--background-image` and the
 * `image` fill type, which would otherwise persist a presigned URL that
 * nothing re-resolves (the save strip only clears URLs behind a pointer).
 */
export function withoutLayerImage<
  L extends { value?: unknown; meta?: unknown; cssVars?: unknown },
>(layer: L): L {
  const cssVars =
    (layer.cssVars as Record<string, unknown> | null | undefined) ?? undefined;
  const bg = cssVars?.["--background-image"];
  const imageFill =
    cssVars?.["--fill-type"] === "image" ||
    (typeof bg === "string" && /^\s*url\(/.test(bg));
  return {
    ...layer,
    value: typeof layer.value === "string" ? "" : layer.value,
    meta: withoutMediaId((layer.meta ?? {}) as object),
    ...(cssVars && imageFill
      ? {
          cssVars: {
            ...cssVars,
            "--fill-type": "none",
            "--background-image": "",
          },
        }
      : {}),
  };
}

/** Remove the media pointer (and any legacy asset pointer). */
export function withoutMediaId<M extends object>(meta: M) {
  const { mediaId: _dropped, ...rest } = stripLegacyAssetPointer(meta) as M & {
    mediaId?: unknown;
  };
  return rest;
}

function stripLegacyAssetPointer<M extends object>(meta: M): M {
  const asset = (meta as { asset?: unknown }).asset;
  if (!asset || typeof asset !== "object") return meta;
  const {
    id: _id,
    preview: _preview,
    ...display
  } = asset as Record<string, unknown>;
  return { ...meta, asset: display };
}
